/* eslint-disable @scandipwa/scandipwa-guidelines/only-render-in-component */
/* eslint-disable react/no-unknown-property */
/**
 * ScandiPWA - Progressive Web App for Magento
 *
 * Copyright © Scandiweb, Inc. All rights reserved.
 * See LICENSE for license details.
 *
 * @license OSL-3.0 (Open Software License ("OSL") v. 3.0)
 * @package scandipwa/base-theme
 * @link https://github.com/scandipwa/base-theme
 */

import PropTypes from 'prop-types';
import React from 'react';

import Draggable from 'Component/Draggable';
import Slider from 'Component/Slider/Slider.component';
import { DeviceType } from 'Type/Device.type';
import CSS from 'Util/CSS';

import { ANIMATION_DURATION, STOP_SLIDE_COUNT } from './AdditionalPictures.config';

import './AdditionalPictures.style';

/**
 * AdditionalPictures component
 * @class AdditionalPictures
 * @namespace Scandipwa/Component/AdditionalPictures/Component */
export class AdditionalPicturesComponent extends Slider {
    static propTypes = {
        ...super.propTypes,
        isLoading: PropTypes.bool.isRequired,
        device: DeviceType
    };

    static defaultProps = {
        ...super.defaultProps,
        device: null
    };

    componentDidUpdate(prevProps) {
        const { activeImage: prevActiveImage } = prevProps;
        const { activeImage, children: childrenRaw } = this.props;
        const { children: [{ clientHeight: childrenHeight }] } = this.draggableRef.current;

        const children = childrenRaw.flat();
        const childrenCount = children.filter((child) => (child !== false)).length;

        if (activeImage !== prevActiveImage) {
            CSS.setVariable(
                this.draggableRef,
                'animation-speed',
                `${ Math.abs((prevActiveImage - activeImage) * ANIMATION_DURATION) }ms`
            );

            CSS.setVariable(
                this.draggableRef,
                'translateY',
                '0px'
            );

            if (children.length >= STOP_SLIDE_COUNT) {
                CSS.setVariable(
                    this.draggableRef,
                    'translateY',
                    `${-childrenHeight * activeImage}px`
                );
            }

            if (activeImage >= childrenCount - STOP_SLIDE_COUNT && childrenCount >= STOP_SLIDE_COUNT) {
                CSS.setVariable(
                    this.draggableRef,
                    'translateY',
                    `${-childrenHeight * (childrenCount - STOP_SLIDE_COUNT) }px`
                );
            }
        }
    }

    render() {
        const {
            mix,
            children: childrenRaw,
            activeImage,
            isLoading
        } = this.props;

        const children = childrenRaw.flat();
        const childrenCount = children.filter((child) => (child !== false)).length;
        const lastImage = activeImage > childrenCount - activeImage - STOP_SLIDE_COUNT;

        return (
            <div block="AdditionalSlider-Wrapper" mods={ { isLoading } }>
                { this.renderButton('left', childrenCount) }
                <div
                  block="AdditionalSlider"
                  mix={ mix }
                  ref={ this.sliderRef }
                >
                    <Draggable
                      mix={ {
                          block: 'AdditionalSlider',
                          elem: 'Wrapper',
                          mods: { isLoading }
                      } }
                      draggableRef={ this.draggableRef }
                    >
                        { children }
                    </Draggable>
                </div>
                { this.renderButton('right', childrenCount, lastImage) }
            </div>
        );
    }
}

export default AdditionalPicturesComponent;
